/**
 * Relewise core — window.agf.relewiseCore.
 *
 * Every Relewise-powered part of the theme (recommendation rows, cart upsell, search, collections)
 * goes through here. It reuses the Relewise app embed's SDK (window.Relewise, 2.24.0) and its
 * consent-correct user (ShopifyRelewise.getUserAsync) instead of shipping a second SDK copy — so only
 * use builder methods that exist in 2.24, and set newer fields on the built request. Every call returns
 * a promise; callers fall back to their Shopify behaviour when it rejects.
 *
 * ID contract (Relewise app, Product ID Handler "Default", verified 2026-09-30):
 * productId = Shopify product id as a string, variantId = variant SKU.
 */
(() => {
  const cfg = window.agf && window.agf.relewise;
  if (!cfg || !cfg.datasetId || window.agf.relewiseCore) return;

  const HANDLE_KEY = `${cfg.shop}_ShopifyHandle`;
  const APP_WAIT_MS = 3000;
  const BATCH_WINDOW_MS = 50;

  /** Fixed, case-sensitive displayed-at locations: they key analytics and merchandising in My Relewise. */
  const LOCATIONS = {
    front: 'Front Page',
    product: 'Product Details Page',
    category: 'Category Page',
    cart: 'Cart/Basket',
    emptyCart: 'Empty Cart',
    overlay: 'Search Overlay',
    search: 'Search Results Page',
    noResults: 'No Search Results',
    notFound: '404 Page',
  };

  const ready = () => !!(window.Relewise && window.Relewise.Recommender);

  /* The embed assigns ShopifyRelewise on DOMContentLoaded, after the deferred theme scripts, so wait
     for it (bounded). Its user carries the temporary id and the Country classification the embed tracks
     with — requests must use the same identity or personalisation and attribution break. */
  let appReady = null;
  function waitForApp() {
    appReady = appReady || new Promise((resolve) => {
      const deadline = Date.now() + APP_WAIT_MS;
      (function poll() {
        if (window.ShopifyRelewise && typeof window.ShopifyRelewise.getUserAsync === 'function') return resolve(true);
        if (Date.now() > deadline) return resolve(false);
        setTimeout(poll, 50);
      })();
    });
    return appReady;
  }

  function anonymous() {
    const user = Relewise.UserFactory.anonymous();
    user.classifications = { Country: window.Shopify && window.Shopify.country };
    return user;
  }

  /* With Cookiebot as the app's consent provider (Relewise app → Settings), the embed resolves its user on
     Cookiebot's consent callback and waits up to 1.5 s for it. The callback never comes while a visitor hasn't
     answered the banner, nor when Cookiebot was ready before the embed started listening (DOMContentLoaded), so every
     Relewise part would wait those 1.5 s. Cookiebot's answer is already in its CookieConsent cookie (stamp = its
     consentID), so the theme builds the embed's user from it at once: anonymous without marketing consent, the
     consent ID as temporary id with it. A logged-in customer with consent (the embed adds the customer id), another
     provider, or a cookie that can't be read waits for the embed. Read once per page, as the embed does. */
  let cookiebotUser;
  function fromCookiebot() {
    const embed = [...document.querySelectorAll('script:not([src])')].find((s) => s.textContent.includes('cookieConsentProvider'));
    // The embed wrote `cookieConsentProvider = '…'` up to app version 91 and `cookieConsentProvider: '…',` from 92.
    const provider = embed && /cookieConsentProvider\s*[:=]\s*['"](\w+)['"]/.exec(embed.textContent);
    if (!provider || provider[1] !== 'Cookiebot') return null;
    const raw = (document.cookie.match(/(?:^|;\s*)CookieConsent=([^;]*)/) || [])[1];
    if (!raw) return anonymous();
    let value;
    try {
      value = decodeURIComponent(raw);
    } catch (error) {
      return null;
    }
    const marketing = /marketing:(true|false)/.exec(value);
    const stamp = /stamp:'([^']*)'/.exec(value);
    if (!marketing || !stamp) return null;
    if (marketing[1] !== 'true' || !stamp[1] || stamp[1] === '0') return anonymous();
    if (cfg.loggedIn) return null;
    const consented = Relewise.UserFactory.byTemporaryId(stamp[1]);
    consented.classifications = { Country: window.Shopify && window.Shopify.country };
    return consented;
  }

  async function user() {
    if (!ready()) throw new Error('Relewise SDK missing');
    if (cookiebotUser === undefined) {
      cookiebotUser = fromCookiebot();
      // Still ask the embed for its user, without waiting: that early call is what lets the embed resolve Cookiebot's
      // consent in time for its own tracking (product views, cart). Left out, the embed (app 92) often gives up after
      // 1.5 s and tracks a consenting visitor as anonymous (measured 2026-10-23).
      if (cookiebotUser) waitForApp().then((found) => found && window.ShopifyRelewise.getUserAsync()).catch(() => {});
    }
    if (cookiebotUser) return cookiebotUser;
    if (await waitForApp()) {
      try {
        return (await window.ShopifyRelewise.getUserAsync()) || anonymous();
      } catch (error) {
        return anonymous();
      }
    }
    return anonymous();
  }

  const settings = (location, relewiseUser) => ({
    language: cfg.language,
    currency: cfg.currency,
    displayedAtLocation: location,
    user: relewiseUser,
  });

  const clients = {};
  const client = (name) => (clients[name] = clients[name] || new Relewise[name](cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl }));

  /* Recommendation rows that load together go out as ONE request with distinct products across
     rows (Relewise go-live check 2026-09-15). The first request opens a short window; everything
     queued before it closes is batched. A row that resolves later opens a new window. */
  let queue = [];
  let timer = null;

  function flush() {
    timer = null;
    const batch = queue;
    queue = [];
    if (!batch.length) return;
    const builder = new Relewise.ProductsRecommendationCollectionBuilder().requireDistinctProductsAcrossResults(true);
    batch.forEach((item) => builder.addRequest(item.request));
    client('Recommender')
      .batchProductRecommendations(builder.build())
      .then((result) => {
        const responses = (result && result.responses) || [];
        batch.forEach((item, i) => item.resolve(responses[i] || null));
      })
      .catch((error) => batch.forEach((item) => item.reject(error)));
  }

  /** @param {object} request a built product recommendation request → Promise of its own response */
  function recommend(request) {
    return new Promise((resolve, reject) => {
      queue.push({ request, resolve, reject });
      timer = timer || setTimeout(flush, BATCH_WINDOW_MS);
    });
  }

  /* The store's catalogue, applied by every request the theme sends (not by My Relewise merchandising rules,
     so nothing here changes what other Relewise consumers show):
     - published to the Online Store (POS-only products, drafts and old seasons have no PublishedAt);
     - still in this shop (a deleted product loses the shop from ShopifyDomains);
     - no print add-ons (product type Spillertryk);
     - no hidden products and no Locksmith member products (Klub Aros — its locked collection keeps Shopify's
       listing). A new Locksmith product lock needs its tag here. AGF Ungdom (agf_ungdom_2026) is open to everyone
       since Nicolaj removed it from My Relewise's "Skjul tilkøb og skjulte produkter" (2026-10-26).
     Negate the condition, not the filter: filter-level negation is ignored on data filters. */
  const HIDDEN_TAGS = ['hidden', 'hide', 'klubaros-check'];

  function catalogFilters(filters) {
    const { DataValueFactory } = Relewise;
    return filters
      .addProductDataHasKeyFilter(`${cfg.shop}_PublishedAt`)
      .addProductDataFilter('ShopifyDomains', (c) => c.addContainsCondition(DataValueFactory.string(cfg.shop)))
      .addProductDataFilter('ProductType', (c) => c.addEqualsCondition(DataValueFactory.string('Spillertryk'), true), true, false)
      .addProductDataFilter('Tags', (c) => HIDDEN_TAGS.forEach((tag) => c.addContainsCondition(DataValueFactory.string(tag), 'All', true)), true, false);
  }

  /** Search and category pages: sold-out products sink to the bottom (×0.3) but stay findable. */
  function buryUnavailable(relevanceModifiers) {
    const { DataValueFactory, ValueSelectorFactory } = Relewise;
    return relevanceModifiers.addProductDataRelevanceModifier(
      `${cfg.shop}_AvailableForSale`,
      (c) => c.addEqualsCondition(DataValueFactory.boolean(false)),
      ValueSelectorFactory.fixedDoubleValueSelector(0.3),
    );
  }

  /* Recommendations: the catalogue, only sellable products, and never gift cards. */
  function recommendationFilters(filters) {
    const { DataValueFactory } = Relewise;
    return catalogFilters(filters)
      .addProductDataFilter(`${cfg.shop}_AvailableForSale`, (c) => c.addEqualsCondition(DataValueFactory.boolean(true)))
      .addProductDataFilter('ProductType', (c) => c.addEqualsCondition(DataValueFactory.string('Gavekort'), true), true, false)
      .addProductDataFilter('ProductType', (c) => c.addEqualsCondition(DataValueFactory.string('GIST_GIFT_CARD'), true), true, false);
  }

  const handleOf = (result) => result && result.data && result.data[HANDLE_KEY] && result.data[HANDLE_KEY].value;
  const handles = (results) => (results || []).map(handleOf).filter(Boolean);

  /* Cards are rendered by Shopify, so they are the theme's own markup (prices in the visitor's currency,
     badges, Locksmith, partner prices on a partner collection). Product cards come in batches from
     sections/relewise-cards.liquid through the predictive-search endpoint (it never counts as a storefront
     search): every card asked for in the same tick shares one request per 20 handles. The cart's upsell cards come one per product
     (sections/relewise-cart-card.liquid). A card that comes back empty — a product Locksmith hides,
     or one Shopify no longer serves — is dropped. Cached for the page view; a failed request is retried
     on the next call. Product cards Shopify shows as sold out are noted, so recommendations can leave them out:
     Relewise's AvailableForSale counts the physical shop's stock too. */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const BATCH_SIZE = 20; // Shopify's all_products limit per page
  const cardCache = new Map();
  const soldOut = new Set(); // handles
  const cardQueue = new Map(); // collection → Map(handle → resolve)
  let cardTimer = null;

  const usable = (html) => (html && html.includes('/products/') ? html.trim() : '');

  function fetchBatch(collection, handles) {
    const q = encodeURIComponent([collection || '-', ...handles].join(' '));
    const url = `${root}search/suggest?q=${q}&resources%5Btype%5D=query&resources%5Blimit%5D=1&section_id=relewise-cards`;
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Cards: HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const found = {};
        new DOMParser().parseFromString(html, 'text/html').querySelectorAll('template[data-relewise-card]').forEach((template) => {
          found[template.dataset.relewiseCard] = usable(template.innerHTML);
          if (template.hasAttribute('data-sold-out')) soldOut.add(template.dataset.relewiseCard);
        });
        return found;
      });
  }

  function flushCards() {
    cardTimer = null;
    cardQueue.forEach((pending, collection) => {
      const handles = [...pending.keys()];
      for (let i = 0; i < handles.length; i += BATCH_SIZE) {
        const batch = handles.slice(i, i + BATCH_SIZE);
        fetchBatch(collection, batch)
          .then((found) => batch.forEach((handle) => pending.get(handle)(found[handle] || '')))
          .catch(() => batch.forEach((handle) => {
            cardCache.delete(`${collection}|${handle}`);
            pending.get(handle)('');
          }));
      }
    });
    cardQueue.clear();
  }

  function productCard(handle, collection) {
    const key = `${collection}|${handle}`;
    if (!cardCache.has(key)) {
      cardCache.set(key, new Promise((resolve) => {
        if (!cardQueue.has(collection)) cardQueue.set(collection, new Map());
        cardQueue.get(collection).set(handle, resolve);
        cardTimer = cardTimer || setTimeout(flushCards, 0);
      }));
    }
    return cardCache.get(key);
  }

  function cartCard(handle) {
    const key = `cart|${handle}`;
    if (!cardCache.has(key)) {
      cardCache.set(key, fetch(`${root}products/${encodeURIComponent(handle)}?section_id=relewise-cart-card`)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => usable(new DOMParser().parseFromString(html, 'text/html').querySelector('template[data-relewise-cart-card]')?.innerHTML))
        .catch(() => ''));
    }
    return cardCache.get(key);
  }

  const sizedHtml = (html, sizes) => (sizes ? html.replaceAll('sizes="100vw"', `sizes="${sizes.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"`) : html);

  /** → [{ handle, html }] in the given order, empty cards dropped. `sizes`: the image sizes the theme's own cards
      use where these go (product cards come with the theme default, 100vw), so the same image files load.
      `sellable`: leave out products Shopify shows as sold out (recommendations). */
  async function cards(list, { view = 'product', collection = '', sizes = '', sellable = false } = {}) {
    const htmls = await Promise.all(list.map((handle) => (view === 'relewise-cart-card' ? cartCard(handle) : productCard(handle, collection))));
    return list.map((handle, i) => ({ handle, html: htmls[i] && sizedHtml(htmls[i], sizes) }))
      .filter((item) => item.html && !(sellable && soldOut.has(item.handle)));
  }

  /* Products Relewise can't hold (no SKU on any variant, e.g. Shopify Bundles) in a collection, with their place in
     its own order, from sections/relewise-extras.liquid. Collection listings slot them in there. Search has none: it
     runs on Relewise alone. Cached for the page view; none if the request fails. */
  const extrasCache = new Map();
  function extras({ collection = '', sizes = '' } = {}) {
    const url = `${root}collections/${encodeURIComponent(collection)}?section_id=relewise-extras`;
    if (!extrasCache.has(url)) {
      extrasCache.set(url, fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('template[data-relewise-extra]')]
          .map((template) => ({ handle: template.dataset.relewiseExtra, position: Number(template.dataset.position) || 0, html: usable(template.innerHTML) }))
          .filter((item) => item.html))
        .catch(() => []));
    }
    return extrasCache.get(url).then((list) => list.map((item) => ({ ...item, html: sizedHtml(item.html, sizes) })));
  }

  /** Fill the animation tokens the card template leaves (same tokens as sections/api-product-grid-item). */
  const fillTokens = (html, { anchor = '', delay = 0 } = {}) => html
    .replaceAll('||itemAnimationAnchor||', anchor)
    .replaceAll('||itemAnimationDelay||', String(delay));

  /* A variant switch on the product page is a new product view with the new SKU (Relewise go-live
     check 2026-09-15). The embed already tracks the landing view. theme:variant:change also fires from
     quick-add modals and jersey-builder print options, so only variants of this page's product count. */
  if (cfg.product) {
    let lastSku = cfg.product.landingSku;
    document.addEventListener('theme:variant:change', async (event) => {
      const variant = event.detail && event.detail.variant;
      const sku = variant && cfg.product.skus[variant.id];
      if (!sku || sku === lastSku || !ready()) return;
      lastSku = sku;
      try {
        await client('Tracker').trackProductView({ productId: cfg.product.id, variantId: sku, user: await user() });
      } catch (error) {
        // tracking is best-effort
      }
    });
  }

  window.agf.relewiseCore = {
    LOCATIONS,
    HANDLE_KEY,
    ready,
    user,
    settings,
    recommend,
    catalogFilters,
    buryUnavailable,
    recommendationFilters,
    searcher: () => client('Searcher'),
    recommender: () => client('Recommender'),
    handles,
    cards,
    extras,
    fillTokens,
  };
})();
